/**
 * @fileoverview Core MMR (Matchmaking Rating) service for Zodiac Chess
 *
 * Implements a two-phase rating system:
 * - Phase 1: Placement matches (games 1-10) with high volatility
 * - Phase 2: Zero-sum ranked matches with asymmetric punishment
 *
 * Key features:
 * - Starting rating: 1300 (honoring the number 13)
 * - 300-point falloff threshold for rating exchanges
 * - Asymmetric punishment based on win probability curves
 */

import { User } from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import {
  UserProfile,
  MMRData,
  GameResult,
  RatingExchange,
  GameOutcome,
  Player,
  PlacementGame,
  TimeControlType,
  TimeControlRating
} from './types';
import { UserDataService } from '../firebase/userDataService';
import { detectUserRegion } from '../../utils/regionDetection';

export class MMRService {
  // Core constants
  private static readonly START_RATING = 1300;
  private static readonly PLACEMENT_GAMES = 10;
  private static readonly MAX_EXCHANGE = 30;
  private static readonly FALLOFF_THRESHOLD = 300;
  private static readonly MIN_RATING = 500;
  private static readonly MAX_PLACEMENT_RATING = 1900;
  private static readonly REGULAR_K_FACTOR = 20; // K-factor for regular players vs placement

  /**
   * Sync legacy MMR fields from time-control-specific data.
   * Legacy fields (mmr.isPlaced, mmr.placementGames, mmr.displayRating, etc.)
   * are still read by many UI components, so they must stay in sync.
   *
   * Priority: marathon (primary placement) > standard > sprint
   */
  static syncLegacyFields(profile: UserProfile): void {
    const tcRatings = profile.mmr.timeControlRatings;
    if (!tcRatings) return;

    const timeControls: TimeControlType[] = ['sprint', 'standard', 'marathon'];

    // Find the HIGHEST placed rating across all time controls
    let bestPlacedRating = 0;
    let anyPlaced = false;
    let highestPeak = profile.mmr.peak || 0;

    for (const tc of timeControls) {
      const r = tcRatings[tc];
      if (!r) continue;

      // Track highest peak across ALL TCs (placed or not)
      if (r.peak) highestPeak = Math.max(highestPeak, r.peak);

      if (r.isPlaced && r.rating > bestPlacedRating) {
        bestPlacedRating = r.rating;
        anyPlaced = true;
      }
    }

    if (anyPlaced) {
      profile.mmr.isPlaced = true;
      profile.mmr.displayRating = bestPlacedRating;
      profile.mmr.peak = highestPeak;
      profile.mmr.placementGames = this.PLACEMENT_GAMES;
    } else {
      // No TC placed yet — sync from the one with most placement progress
      let bestTC: TimeControlRating | undefined;
      for (const tc of timeControls) {
        const r = tcRatings[tc];
        if (r && (!bestTC || r.placementGames > bestTC.placementGames)) {
          bestTC = r;
        }
      }
      if (bestTC) {
        profile.mmr.placementGames = bestTC.placementGames;
        profile.mmr.provisionalRating = bestTC.provisionalRating;
      }
    }
  }

  /**
   * Initialize a new user profile with default MMR data
   */
  static async initializeUserMMR(user: User): Promise<UserProfile> {
    const now = new Date();

    const mmrData: MMRData = {
      // Legacy fields (kept for backwards compatibility)
      provisionalRating: this.START_RATING,
      placementGames: 0,
      placementHistory: [],
      displayRating: 0,
      rankedGames: 0,
      peak: 0,
      isPlaced: false,

      // NEW: Initialize Marathon placement (new users start here)
      timeControlRatings: {
        marathon: {
          rating: this.START_RATING,
          games: 0,
          peak: this.START_RATING,
          isPlaced: false,
          placementGames: 0,
          provisionalRating: this.START_RATING
        }
        // Sprint and Standard will be initialized on first play
      },

      // Meta
      totalGames: 0,
      createdAt: now,
      colorBalance: 0
    };

    // Detect user region automatically
    const regionInfo = detectUserRegion();

    const profile: UserProfile = {
      mmr: mmrData,
      username: await this.generateUniqueUsername(user.displayName || user.email || ''),
      lastActive: now,
      region: regionInfo.region,
      regionDetectedBy: regionInfo.detectionMethod
    };

    // Add optional fields only if they have values (Firebase doesn't like undefined)
    if (user.displayName) {
      profile.displayName = user.displayName;
    }

    try {
      // Save in the structure expected by UserDataService.getUserData()
      // This ensures processGameResult can read the profile correctly
      const userData = {
        uid: user.uid,
        profile,
        gamesPlayed: 0,
        createdAt: now,
        lastActive: now,
        accountType: 'anonymous' as const
      };
      await setDoc(doc(db, 'users', user.uid), userData);
      return profile;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get user profile with MMR data
   */
  static async getUserProfile(uid: string): Promise<UserProfile | null> {
    try {
      const docRef = doc(db, 'users', uid);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const docData = docSnap.data();

      // Handle both document structures:
      // 1. Nested: { profile: { mmr: {...} } } (from UserDataService/initializeUserMMR)
      // 2. Flat: { mmr: {...} } (legacy format)
      const data = docData.profile ? docData.profile as UserProfile : docData as UserProfile;

      // Validate MMR data exists
      if (!data.mmr) {
        return null;
      }

      // Convert Firestore timestamps to Date objects
      if (data.mmr?.createdAt && typeof data.mmr.createdAt !== 'object') {
        data.mmr.createdAt = new Date(data.mmr.createdAt);
      }
      if (data.mmr?.lastGameDate && typeof data.mmr.lastGameDate !== 'object') {
        data.mmr.lastGameDate = new Date(data.mmr.lastGameDate);
      }
      if (data.lastActive && typeof data.lastActive !== 'object') {
        data.lastActive = new Date(data.lastActive);
      }

      return data;
    } catch (error) {
      return null;
    }
  }

  /**
   * Update user profile in database
   */
  static async updateUserProfile(uid: string, profile: UserProfile): Promise<void> {
    try {
      profile.lastActive = new Date();
      // Update the nested profile field to match the document structure
      await updateDoc(doc(db, 'users', uid), {
        profile,
        lastActive: new Date(),
        gamesPlayed: profile.mmr.totalGames
      } as any);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get basic user info (username) without requiring MMR data
   * Returns partial profile with at least username, even if MMR not initialized
   */
  static async getBasicUserInfo(uid: string): Promise<{ username: string } | null> {
    try {
      const docRef = doc(db, 'users', uid);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return {
        username: data.username || 'Unknown'
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * Calculate win probability using ELO formula
   */
  static calculateWinProbability(ratingA: number, ratingB: number): number {
    const ratingDiff = ratingA - ratingB;
    return 1 / (1 + Math.pow(10, -ratingDiff / 400));
  }

  /**
   * Calculate placement match rating change (high volatility)
   * Allows extreme swings: 800-900 range for 0-10 record, 1700-1800 for 10-0 record
   */
  static calculatePlacementChange(
    provisionalRating: number,
    opponentRating: number,
    result: GameOutcome,
    gameNumber: number
  ): number {
    // Falloff: no rating gain for favorites beating much weaker opponents
    // Matches ranked system's FALLOFF_THRESHOLD (300 points)
    // Only blocks wins — losses against weaker opponents still count
    // (so overrated players get corrected downward)
    if (provisionalRating - opponentRating > this.FALLOFF_THRESHOLD && result === 'win') {
      return 0;
    }

    const expectedScore = this.calculateWinProbability(provisionalRating, opponentRating);
    // For draws, actualScore = 0.5 (better than expected for underdog, worse for favorite)
    const actualScore = result === 'win' ? 1 : result === 'draw' ? 0.5 : 0;

    // Increased volatility for faster placement: Game 1 = ±100, Game 10 = ±40
    // This allows reaching 1700-1800 with perfect record or 800-900 with 0-10
    const volatility = Math.max(100 - ((gameNumber - 1) * 6), 40);

    return Math.round(volatility * (actualScore - expectedScore));
  }

  /**
   * Calculate regular player rating change when facing a placement player (non-zero-sum)
   * Uses K-factor approach for regular players to maintain consistency
   */
  static calculateRegularPlayerChange(
    regularRating: number,
    placementRating: number,
    result: GameOutcome
  ): number {
    const expectedScore = this.calculateWinProbability(regularRating, placementRating);
    const actualScore = result === 'win' ? 1 : result === 'draw' ? 0.5 : 0;

    return Math.round(this.REGULAR_K_FACTOR * (actualScore - expectedScore));
  }

  /**
   * Calculate zero-sum rating exchange for ranked matches
   */
  static calculateRatingExchange(
    higherRating: number,
    lowerRating: number,
    winner: 'higher' | 'lower' | 'draw'
  ): RatingExchange {
    const ratingDiff = Math.abs(higherRating - lowerRating);

    // Beyond falloff threshold = no rating change
    if (ratingDiff > this.FALLOFF_THRESHOLD) {
      return { higherChange: 0, lowerChange: 0 };
    }

    // Calculate win probabilities
    const higherWinProb = this.calculateWinProbability(higherRating, lowerRating);
    const lowerWinProb = 1 - higherWinProb;

    if (winner === 'draw') {
      // For draws: underdog gains slightly, favorite loses slightly
      // Draw is worth 0.5 points, so calculate as if each got 0.5
      const exchange = Math.round(this.MAX_EXCHANGE * (0.5 - higherWinProb));
      return { higherChange: exchange, lowerChange: -exchange };
    }

    // Exchange based on surprise factor (asymmetric punishment)
    const surpriseFactor = winner === 'higher' ? lowerWinProb : higherWinProb;
    const exchange = Math.round(this.MAX_EXCHANGE * surpriseFactor);

    if (winner === 'higher') {
      return { higherChange: +exchange, lowerChange: -exchange };
    } else {
      return { higherChange: -exchange, lowerChange: +exchange };
    }
  }

  /**
   * Get current rating for a user (provisional or display)
   */
  static getCurrentRating(profile: UserProfile): number {
    return profile.mmr.isPlaced
      ? profile.mmr.displayRating
      : profile.mmr.provisionalRating;
  }

  /**
   * Check if a user needs profile initialization
   */
  static async needsInitialization(user: User): Promise<boolean> {
    const profile = await this.getUserProfile(user.uid);
    return profile === null;
  }

  /**
   * Record a game result in user's history
   */
  static async recordGameHistory(
    userId: string,
    gameResult: Omit<GameResult, 'gameDate'>
  ): Promise<void> {
    try {
      const gameWithDate: GameResult = {
        ...gameResult,
        gameDate: new Date(),
        // CRITICAL: Firebase doesn't allow undefined values - default to 'competitive' for old games
        matchmakingMode: gameResult.matchmakingMode || 'competitive'
      };

      await addDoc(collection(db, 'users', userId, 'games'), gameWithDate);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Validate rating bounds
   */
  static clampRating(rating: number, isPlacement: boolean = false): number {
    const min = this.MIN_RATING;
    const max = isPlacement ? this.MAX_PLACEMENT_RATING : Infinity;
    return Math.max(min, Math.min(max, rating));
  }

  /**
   * Update user's zodiac result
   */
  static async updateZodiacResult(uid: string, zodiacResult: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'users', uid), {
        zodiacResult: zodiacResult
      } as any);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Generate a unique username with profanity filtering
   */
  static async generateUniqueUsername(displayName: string): Promise<string> {
    const naughtyWords = [
      'fuck', 'shit', 'damn', 'hell', 'ass', 'bitch', 'bastard', 'crap',
      'piss', 'dick', 'cock', 'pussy', 'whore', 'slut', 'nazi', 'hitler',
      'kill', 'die', 'murder', 'rape', 'sex', 'porn', 'xxx', 'gay', 'fag',
      'retard', 'stupid', 'idiot', 'moron', 'loser', 'noob', 'scrub'
    ];

    const cleanName = (name: string): string => {
      return name
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .substring(0, 12);
    };

    const containsNaughtyWords = (name: string): boolean => {
      const lowerName = name.toLowerCase();
      return naughtyWords.some(word => lowerName.includes(word));
    };

    // Start with display name or email prefix
    let baseName = cleanName(displayName.split('@')[0]);

    if (!baseName || baseName.length < 3) {
      baseName = 'player';
    }

    // Check for profanity and replace if needed
    if (containsNaughtyWords(baseName)) {
      const cleanAlternatives = ['player', 'gamer', 'user', 'zodiac', 'chess'];
      baseName = cleanAlternatives[Math.floor(Math.random() * cleanAlternatives.length)];
    }

    // Generate unique username by checking existing users
    let username = baseName;
    let attempts = 0;
    const maxAttempts = 50;

    while (attempts < maxAttempts) {
      try {
        // Check if username exists by querying users collection
        const existingUser = await this.findUserByUsername(username);

        if (!existingUser) {
          return username;
        }

        // Username taken, generate new one
        const randomNum = Math.floor(Math.random() * 9999);
        username = `${baseName}${randomNum}`;
        attempts++;
      } catch (error) {
        // Fallback to random username
        const randomNum = Math.floor(Math.random() * 9999);
        return `player${randomNum}`;
      }
    }

    // Fallback if all attempts failed
    const randomNum = Math.floor(Math.random() * 99999);
    return `player${randomNum}`;
  }

  /**
   * Determine color assignment based on color balance
   * Higher balance = needs to play dark, lower = needs to play light
   * Same balance = 50/50 random
   */
  static determineColorAssignment(
    player1Profile: UserProfile,
    player2Profile: UserProfile
  ): { player1IsLight: boolean } {
    const balance1 = player1Profile.mmr.colorBalance || 0;
    const balance2 = player2Profile.mmr.colorBalance || 0;

    if (balance1 > balance2) {
      // Player 1 has played more light games, so they should be dark
      return { player1IsLight: false };
    } else if (balance2 > balance1) {
      // Player 2 has played more light games, so they should be dark
      return { player1IsLight: true };
    } else {
      // Equal balance - 50/50 random
      return { player1IsLight: Math.random() < 0.5 };
    }
  }

  /**
   * Find user by username (helper for username uniqueness)
   */
  private static async findUserByUsername(username: string): Promise<UserProfile | null> {
    try {
      // This is a simplified check - in production you'd want to use a more efficient index
      // For now, we'll assume usernames are unique enough with the random numbers
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get time control type from TimeControlPreset string
   */
  private static getTimeControlType(preset: string): TimeControlType {
    if (preset === 'sprint') return 'sprint';
    if (preset === 'marathon') return 'marathon';
    return 'standard'; // Default for 'off', 'any', etc.
  }

  /**
   * Initialize a fresh time control rating (first game in this time control)
   * @param baselineRating - Optional baseline rating to start from (for secondary placements)
   */
  private static initializeTimeControlRating(baselineRating?: number): TimeControlRating {
    const startRating = baselineRating || this.START_RATING;
    return {
      rating: startRating,
      games: 0,
      peak: startRating,
      isPlaced: false,
      placementGames: 0,
      provisionalRating: startRating
    };
  }

  /**
   * Get or create time control rating for a user
   * Ensures timeControlRatings object and specific rating exist
   * For secondary placements (Sprint/Standard), inherits Marathon ELO as baseline
   */
  private static getOrCreateTimeControlRating(
    profile: UserProfile,
    timeControl: TimeControlType
  ): TimeControlRating {
    // Ensure timeControlRatings object exists
    if (!profile.mmr.timeControlRatings) {
      profile.mmr.timeControlRatings = {};
    }

    // Get or create rating for this time control
    if (!profile.mmr.timeControlRatings[timeControl]) {
      // For secondary placements (Sprint/Standard), inherit Marathon ELO as baseline
      let baselineRating = this.START_RATING;

      if (timeControl !== 'marathon') {
        const marathonRating = profile.mmr.timeControlRatings.marathon;
        if (marathonRating?.isPlaced) {
          baselineRating = marathonRating.rating;
        }
      }

      profile.mmr.timeControlRatings[timeControl] = this.initializeTimeControlRating(baselineRating);
    }

    return profile.mmr.timeControlRatings[timeControl]!;
  }

  /**
   * Get current rating for matchmaking (time-control-specific)
   * Used when adding user to matchmaking queue
   */
  static getCurrentRatingForTimeControl(
    profile: UserProfile,
    timeControl: TimeControlType
  ): number {
    const tcRating = profile.mmr.timeControlRatings?.[timeControl];

    if (!tcRating) {
      return this.START_RATING; // First game in this time control
    }

    return tcRating.isPlaced ? tcRating.rating : tcRating.provisionalRating;
  }

  /**
   * Get highest rating across all time controls for display
   * Returns rating, time control, and placement status
   */
  static getHighestRating(profile: UserProfile): {
    rating: number;
    timeControl: TimeControlType;
    isPlaced: boolean;
  } {
    const tcRatings = profile.mmr.timeControlRatings;

    if (!tcRatings) {
      // Fallback to legacy rating during migration
      return {
        rating: profile.mmr.isPlaced ? profile.mmr.displayRating : profile.mmr.provisionalRating,
        timeControl: 'standard',
        isPlaced: profile.mmr.isPlaced
      };
    }

    let highestRating = 0;
    let highestTimeControl: TimeControlType = 'standard';
    let highestIsPlaced = false;

    // Check all time controls - prioritize placed ratings
    (['sprint', 'standard', 'marathon'] as TimeControlType[]).forEach(tc => {
      const rating = tcRatings[tc];
      if (rating && rating.isPlaced) {
        const displayRating = rating.rating;
        if (displayRating > highestRating) {
          highestRating = displayRating;
          highestTimeControl = tc;
          highestIsPlaced = true;
        }
      }
    });

    // If no placed ratings, return highest provisional
    if (!highestIsPlaced) {
      (['sprint', 'standard', 'marathon'] as TimeControlType[]).forEach(tc => {
        const rating = tcRatings[tc];
        if (rating) {
          const provisionalRating = rating.provisionalRating;
          if (provisionalRating > highestRating) {
            highestRating = provisionalRating;
            highestTimeControl = tc;
          }
        }
      });
    }

    return {
      rating: highestRating || this.START_RATING,
      timeControl: highestTimeControl,
      isPlaced: highestIsPlaced
    };
  }

  /**
   * Check if user is in Marathon placement phase
   * New users must complete 10 Marathon games before playing other time controls
   */
  static isInMarathonPlacement(profile: UserProfile): boolean {
    const marathonRating = profile.mmr.timeControlRatings?.marathon;

    if (!marathonRating) {
      return true; // New user, needs Marathon placement
    }

    return !marathonRating.isPlaced && marathonRating.placementGames < this.PLACEMENT_GAMES;
  }

  /**
   * Process game result and update MMR for both players
   * @param matchmakingMode - Optional matchmaking mode ('casual' games skip ELO changes)
   * @param timeControlPreset - Optional time control preset (sprint/standard/marathon)
   * @param lightPlayerMode - Optional per-player matchmaking mode for light player
   * @param darkPlayerMode - Optional per-player matchmaking mode for dark player
   * @returns GameResult objects for both players (lightPlayerResult, darkPlayerResult)
   */
  static async processGameResult(
    gameId: string,
    lightPlayerId: string,
    darkPlayerId: string,
    winner: 'light' | 'dark' | 'draw',
    matchmakingMode?: 'competitive' | 'casual',
    timeControlPreset?: string,  // Time control used in this game
    lightPlayerMode?: 'competitive' | 'casual' | 'placement',  // Per-player mode for cross-mode matching
    darkPlayerMode?: 'competitive' | 'casual' | 'placement'   // Per-player mode for cross-mode matching
  ): Promise<{ lightPlayerResult: GameResult; darkPlayerResult: GameResult }> {

    // Detect cross-mode match: one casual player vs one placement player
    const isCrossModeMatch = (lightPlayerMode === 'casual' && darkPlayerMode === 'placement') ||
                             (lightPlayerMode === 'placement' && darkPlayerMode === 'casual');

    // Check if this is a pure casual game (both players casual, or legacy single mode)
    const isCasualGame = matchmakingMode === 'casual' && !isCrossModeMatch;


    // Fetch both player profiles using UserDataService (Firebase-first approach)
    const [lightUserData, darkUserData] = await Promise.all([
      UserDataService.getUserData(lightPlayerId),
      UserDataService.getUserData(darkPlayerId)
    ]);

    let lightProfile = lightUserData?.profile || null;
    let darkProfile = darkUserData?.profile || null;

    // Log the colorBalance values fetched from Firebase

    // If profiles don't exist, this is a critical error but we can try to initialize them
    if (!lightProfile) {
      try {
        const lightUser = { uid: lightPlayerId, displayName: null, email: null } as any;
        lightProfile = await this.initializeUserMMR(lightUser);
      } catch (initError) {
        throw new Error(`Failed to initialize MMR profile for light player ${lightPlayerId}`);
      }
    }

    if (!darkProfile) {
      try {
        const darkUser = { uid: darkPlayerId, displayName: null, email: null } as any;
        darkProfile = await this.initializeUserMMR(darkUser);
      } catch (initError) {
        throw new Error(`Failed to initialize MMR profile for dark player ${darkPlayerId}`);
      }
    }

    // Determine game outcome for each player
    const lightResult: GameOutcome = winner === 'light' ? 'win' : winner === 'dark' ? 'loss' : 'draw';
    const darkResult: GameOutcome = winner === 'dark' ? 'win' : winner === 'light' ? 'loss' : 'draw';

    // Get current ratings
    const lightRatingBefore = this.getCurrentRating(lightProfile);
    const darkRatingBefore = this.getCurrentRating(darkProfile);

    let lightRatingAfter = lightRatingBefore;
    let darkRatingAfter = darkRatingBefore;
    let lightRatingChange = 0;
    let darkRatingChange = 0;

    // Determine time control for this game
    const timeControl = this.getTimeControlType(timeControlPreset || 'standard');

    // Get or create time control ratings for both players
    const lightTCRating = this.getOrCreateTimeControlRating(lightProfile, timeControl);
    const darkTCRating = this.getOrCreateTimeControlRating(darkProfile, timeControl);

    // Save pre-game placement status (before processing or auto-repair changes it)
    // Used by determineGameMode() so the 10th placement game shows as placement, not ranked
    const lightWasInPlacement = !lightTCRating.isPlaced;
    const darkWasInPlacement = !darkTCRating.isPlaced;

    // AUTO-REPAIR: Fix corrupted profiles where placementGames >= 10 but isPlaced is false
    if (lightTCRating.placementGames >= this.PLACEMENT_GAMES && !lightTCRating.isPlaced) {
      lightTCRating.isPlaced = true;
      lightTCRating.rating = lightTCRating.provisionalRating;
      lightTCRating.peak = Math.max(lightTCRating.peak || 0, lightTCRating.rating);
      // Also fix legacy fields
      lightProfile.mmr.isPlaced = true;
      lightProfile.mmr.displayRating = lightTCRating.rating;
    }
    if (darkTCRating.placementGames >= this.PLACEMENT_GAMES && !darkTCRating.isPlaced) {
      darkTCRating.isPlaced = true;
      darkTCRating.rating = darkTCRating.provisionalRating;
      darkTCRating.peak = Math.max(darkTCRating.peak || 0, darkTCRating.rating);
      // Also fix legacy fields
      darkProfile.mmr.isPlaced = true;
      darkProfile.mmr.displayRating = darkTCRating.rating;
    }

    // Get current ratings for this time control
    const lightTCRatingBefore = lightTCRating.isPlaced ? lightTCRating.rating : lightTCRating.provisionalRating;
    const darkTCRatingBefore = darkTCRating.isPlaced ? darkTCRating.rating : darkTCRating.provisionalRating;

    // CROSS-MODE MATCH: Casual player vs Placement player
    // Only update the placement player's rating, using casual player's Marathon ELO
    if (isCrossModeMatch) {

      // Determine which player is placement and which is casual
      const lightIsPlacement = lightPlayerMode === 'placement';
      const placementProfile = lightIsPlacement ? lightProfile : darkProfile;
      const casualProfile = lightIsPlacement ? darkProfile : lightProfile;
      const placementTCRating = lightIsPlacement ? lightTCRating : darkTCRating;
      const placementResult = lightIsPlacement ? lightResult : darkResult;
      const placementPlayerId = lightIsPlacement ? lightPlayerId : darkPlayerId;

      // Get casual player's Marathon ELO as opponent rating
      const casualMarathonRating = casualProfile.mmr.timeControlRatings?.marathon;
      const casualEloForPlacement = casualMarathonRating?.isPlaced
        ? casualMarathonRating.rating
        : (casualMarathonRating?.provisionalRating || this.START_RATING);


      // Only update placement player's rating
      if (!placementTCRating.isPlaced) {
        const gameNumber = placementTCRating.placementGames + 1;
        const placementRatingChange = this.calculatePlacementChange(
          placementTCRating.provisionalRating,
          casualEloForPlacement,  // Use casual's Marathon ELO
          placementResult,
          gameNumber
        );
        const placementRatingAfter = this.clampRating(
          placementTCRating.provisionalRating + placementRatingChange,
          true
        );

        // Update placement data
        placementTCRating.provisionalRating = placementRatingAfter;
        placementTCRating.placementGames = gameNumber;
        placementTCRating.games += 1;

        // Record placement game result in history for W/L/D indicators
        if (!placementProfile.mmr.placementHistory) placementProfile.mmr.placementHistory = [];
        placementProfile.mmr.placementHistory.push({
          opponentRating: casualEloForPlacement,
          result: placementResult as 'win' | 'loss' | 'draw',
          ratingChange: placementRatingChange,
          gameDate: new Date(),
          gameNumber,
          timeControl,
        });

        // Check if placement complete
        if (gameNumber >= this.PLACEMENT_GAMES) {
          placementTCRating.isPlaced = true;
          placementTCRating.rating = placementRatingAfter;
          placementTCRating.peak = placementRatingAfter;
        }

        // Set the rating changes for result objects
        if (lightIsPlacement) {
          lightRatingChange = placementRatingChange;
          lightRatingAfter = placementRatingAfter;
        } else {
          darkRatingChange = placementRatingChange;
          darkRatingAfter = placementRatingAfter;
        }

      }

      // Sync legacy fields before saving
      this.syncLegacyFields(placementProfile);

      // Save placement player profile only
      try {
        await UserDataService.updateUserProfile(placementPlayerId, placementProfile);
      } catch (saveError) {
        throw saveError;
      }
    }
    // Only process rating changes for ranked games (not casual or cross-mode)
    else if (!isCasualGame) {
      // Process Light Player
      if (!lightTCRating.isPlaced) {
        // Light player is in placement for this time control
        const gameNumber = lightTCRating.placementGames + 1;
        lightRatingChange = this.calculatePlacementChange(
          lightTCRating.provisionalRating,
          darkTCRatingBefore,
          lightResult,
          gameNumber
        );
        lightRatingAfter = this.clampRating(lightTCRating.provisionalRating + lightRatingChange, true);

        // Update placement data for this time control
        lightTCRating.provisionalRating = lightRatingAfter;
        lightTCRating.placementGames = gameNumber;
        lightTCRating.games += 1;

        // Record placement game result in history for W/L/D indicators
        if (!lightProfile.mmr.placementHistory) lightProfile.mmr.placementHistory = [];
        lightProfile.mmr.placementHistory.push({
          opponentRating: darkTCRatingBefore,
          result: lightResult as 'win' | 'loss' | 'draw',
          ratingChange: lightRatingChange,
          gameDate: new Date(),
          gameNumber,
          timeControl,
        });

        // Check if placement complete
        if (gameNumber >= this.PLACEMENT_GAMES) {
          lightTCRating.isPlaced = true;
          lightTCRating.rating = lightRatingAfter;
          lightTCRating.peak = lightRatingAfter;
        }

        // If opponent is ranked, calculate their change using K-factor (non-zero-sum)
        if (darkTCRating.isPlaced) {
          darkRatingChange = this.calculateRegularPlayerChange(
            darkTCRating.rating,
            lightTCRating.provisionalRating,
            darkResult
          );
          darkRatingAfter = this.clampRating(darkTCRating.rating + darkRatingChange);
          darkTCRating.rating = darkRatingAfter;
          darkTCRating.games += 1;

          // Update peak if rating increased
          if (darkRatingAfter > darkTCRating.peak) {
            darkTCRating.peak = darkRatingAfter;
          }
        }
      } else {
        // Light player is ranked in this time control
        lightTCRating.games += 1;
      }

      // Process Dark Player
      if (!darkTCRating.isPlaced) {
        // Dark player is in placement for this time control
        const gameNumber = darkTCRating.placementGames + 1;
        darkRatingChange = this.calculatePlacementChange(
          darkTCRating.provisionalRating,
          lightTCRatingBefore,
          darkResult,
          gameNumber
        );
        darkRatingAfter = this.clampRating(darkTCRating.provisionalRating + darkRatingChange, true);

        // Update placement data for this time control
        darkTCRating.provisionalRating = darkRatingAfter;
        darkTCRating.placementGames = gameNumber;
        darkTCRating.games += 1;

        // Record placement game result in history for W/L/D indicators
        if (!darkProfile.mmr.placementHistory) darkProfile.mmr.placementHistory = [];
        darkProfile.mmr.placementHistory.push({
          opponentRating: lightTCRatingBefore,
          result: darkResult as 'win' | 'loss' | 'draw',
          ratingChange: darkRatingChange,
          gameDate: new Date(),
          gameNumber,
          timeControl,
        });

        // Check if placement complete
        if (gameNumber >= this.PLACEMENT_GAMES) {
          darkTCRating.isPlaced = true;
          darkTCRating.rating = darkRatingAfter;
          darkTCRating.peak = darkRatingAfter;
        }

        // If opponent is ranked, calculate their change using K-factor (non-zero-sum)
        if (lightTCRating.isPlaced) {
          lightRatingChange = this.calculateRegularPlayerChange(
            lightTCRating.rating,
            darkTCRating.provisionalRating,
            lightResult
          );
          lightRatingAfter = this.clampRating(lightTCRating.rating + lightRatingChange);
          lightTCRating.rating = lightRatingAfter;

          // Update peak if rating increased
          if (lightRatingAfter > lightTCRating.peak) {
            lightTCRating.peak = lightRatingAfter;
          }
        }
      } else if (lightTCRating.isPlaced) {
        // Dark player is also ranked in this time control
        darkTCRating.games += 1;
      }

      // CRITICAL: Calculate rating exchange ONCE for both ranked players (zero-sum)
      if (lightTCRating.isPlaced && darkTCRating.isPlaced) {
        const higherRating = Math.max(lightTCRatingBefore, darkTCRatingBefore);
        const lowerRating = Math.min(lightTCRatingBefore, darkTCRatingBefore);

        // Determine the winner parameter for calculateRatingExchange
        let winnerParam: 'higher' | 'lower' | 'draw';
        if (winner === 'draw') {
          winnerParam = 'draw';
        } else {
          const winnerIsHigher = (winner === 'light' && lightTCRatingBefore > darkTCRatingBefore) ||
                                 (winner === 'dark' && darkTCRatingBefore > lightTCRatingBefore);
          winnerParam = winnerIsHigher ? 'higher' : 'lower';
        }

        // Calculate exchange ONCE to ensure zero-sum
        const exchange = this.calculateRatingExchange(higherRating, lowerRating, winnerParam);

        // Apply to both players based on who is higher/lower
        if (lightTCRatingBefore > darkTCRatingBefore) {
          // Light is higher, Dark is lower
          lightRatingChange = exchange.higherChange;
          darkRatingChange = exchange.lowerChange;
        } else if (darkTCRatingBefore > lightTCRatingBefore) {
          // Dark is higher, Light is lower
          darkRatingChange = exchange.higherChange;
          lightRatingChange = exchange.lowerChange;
        } else {
          // Equal ratings - no change for draws, symmetric for wins
          lightRatingChange = exchange.higherChange;
          darkRatingChange = exchange.lowerChange;
        }

        // Apply changes
        lightRatingAfter = this.clampRating(lightTCRating.rating + lightRatingChange);
        darkRatingAfter = this.clampRating(darkTCRating.rating + darkRatingChange);

        lightTCRating.rating = lightRatingAfter;
        darkTCRating.rating = darkRatingAfter;

        // Only update peak if rating increased
        if (lightRatingAfter > lightTCRating.peak) {
          lightTCRating.peak = lightRatingAfter;
        }
        if (darkRatingAfter > darkTCRating.peak) {
          darkTCRating.peak = darkRatingAfter;
        }
      }

      // Sync legacy fields for backwards compatibility (all time controls, not just standard)
      this.syncLegacyFields(lightProfile);
      this.syncLegacyFields(darkProfile);
    } // End of !isCasualGame block

    // Update total games and last game date for both
    lightProfile.mmr.totalGames += 1;
    lightProfile.mmr.lastGameDate = new Date();
    darkProfile.mmr.totalGames += 1;
    darkProfile.mmr.lastGameDate = new Date();

    // Update color balance (light +1, dark -1)
    const lightColorBefore = lightProfile.mmr.colorBalance || 0;
    const darkColorBefore = darkProfile.mmr.colorBalance || 0;
    lightProfile.mmr.colorBalance = lightColorBefore + 1;
    darkProfile.mmr.colorBalance = darkColorBefore - 1;


    // Save both profiles to Firebase using UserDataService (preserves all fields like zodiacResult)
    // Skip saving for casual games since no ratings changed (but still save colorBalance!)
    // BUG FIX: Always save colorBalance updates, even for casual games
    try {

      await Promise.all([
        UserDataService.updateUserProfile(lightPlayerId, lightProfile),
        UserDataService.updateUserProfile(darkPlayerId, darkProfile)
      ]);

    } catch (saveError) {
      throw saveError;
    }

    // Create GameResult objects

    // Determine game mode - uses per-player matchmaking mode for cross-mode match support
    const determineGameMode = (
      profile: UserProfile,
      playerMatchmakingMode?: 'competitive' | 'casual' | 'placement',
      wasInPlacement?: boolean
    ): 'placement' | 'ranked' | 'casual' => {

      // If player queued as casual, show casual modal (no ELO change for them)
      if (playerMatchmakingMode === 'casual') {
        return 'casual';
      }

      // FALLBACK: If the game itself is casual, this player should see the casual modal —
      // unless they are an explicit placement player in a cross-mode match (handled separately).
      // This catches cases where per-player mode was defaulted to 'competitive' (e.g. friend
      // games where savedMatchmakingMode wasn't synced from Firebase on the joiner side).
      if (matchmakingMode === 'casual' && playerMatchmakingMode !== 'placement') {
        return 'casual';
      }

      // If player queued as placement, show placement modal
      if (playerMatchmakingMode === 'placement') {
        return 'placement';
      }

      // Use pre-game placement status if available (prevents 10th game showing as ranked)
      // Processing may have already flipped isPlaced to true, but the game started as placement
      if (wasInPlacement !== undefined) {
        const mode = wasInPlacement ? 'placement' : 'ranked';
        return mode;
      }

      // Check time-control specific placement status (primary source of truth)
      const tcRating = profile.mmr.timeControlRatings?.[timeControl];
      if (tcRating?.isPlaced && tcRating.placementGames >= this.PLACEMENT_GAMES) {
        return 'ranked';
      }

      // If this TC exists but is NOT placed, it's a placement game
      // (Don't fall through to legacy fields which may say "placed" from a different TC)
      if (tcRating && !tcRating.isPlaced) {
        return 'placement';
      }

      // Fallback to legacy fields for backwards compatibility (no TC-specific data exists)
      if (profile.mmr.isPlaced && profile.mmr.placementGames >= this.PLACEMENT_GAMES) {
        return 'ranked';
      }

      return 'placement';
    };

    const lightPlayerResult: GameResult = {
      gameId,
      opponentId: darkPlayerId, // Opponent's player ID
      opponentRating: darkTCRatingBefore, // Use time control specific rating
      result: lightResult,
      ratingBefore: lightTCRatingBefore, // Use time control specific rating
      ratingAfter: lightRatingAfter,
      ratingChange: lightRatingChange,
      gameDate: new Date(),
      gameMode: determineGameMode(lightProfile, lightPlayerMode, lightWasInPlacement),
      placementGamesCompleted: lightTCRating.placementGames, // Use time control specific placement progress
      matchmakingMode: matchmakingMode // Include matchmaking mode for UI display
    };

    const darkPlayerResult: GameResult = {
      gameId,
      opponentId: lightPlayerId,
      opponentRating: lightTCRatingBefore, // Use time control specific rating
      result: darkResult,
      ratingBefore: darkTCRatingBefore, // Use time control specific rating
      ratingAfter: darkRatingAfter,
      ratingChange: darkRatingChange,
      gameDate: new Date(),
      gameMode: determineGameMode(darkProfile, darkPlayerMode, darkWasInPlacement),
      placementGamesCompleted: darkTCRating.placementGames, // Use time control specific placement progress
      matchmakingMode: matchmakingMode // Include matchmaking mode for UI display
    };

    // Record game history for both players
    try {
      await Promise.all([
        this.recordGameHistory(lightPlayerId, lightPlayerResult),
        this.recordGameHistory(darkPlayerId, darkPlayerResult)
      ]);
    } catch (historyError) {
      // Don't throw - game history is not critical
    }


    return { lightPlayerResult, darkPlayerResult };
  }
}

export default MMRService;